import React, { useState, useCallback, useEffect } from "react";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    Select,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    PersonAdd as PersonAddIcon,
    PersonRemove as PersonRemoveIcon,
    Logout as LeaveIcon,
} from "@mui/icons-material";
import { FormattedMessage, useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import insightsApi from "../../api/insightsApi";

export const ROLES = ["owner", "editor", "viewer"];

// How long the add-member search waits after the last keystroke before asking
// the BE — it is a type-ahead over the organization's users.
const SEARCH_DEBOUNCE_MS = 300;

const displayName = (member) =>
    [member.first_name, member.last_name].filter(Boolean).join(" ") || member.email;

// ── Project members ──────────────────────────────────────────────────────────
// Who can see a project. Membership shares EVERYTHING in it — every report
// that is a wave of it and every document — so the dialog says so above the
// list. Only staff and the project's owners and editors open it (the BE
// refuses everyone else): they add people (from the project's organization
// only; the BE picks the candidates), change roles and remove people, and may
// leave. Editors cannot grant, change or remove the owner role.
//
// `onLeft` fires when the caller removed themselves: the project is no longer
// theirs to show, so the page reloads its list.
export default function ProjectMembersDialog({ project, currentUserEmail, onClose, onLeft }) {
    const intl = useIntl();
    const { enqueueSnackbar } = useSnackbar();

    const [members, setMembers] = useState([]);
    const [canManage, setCanManage] = useState(!!project?.can_manage_members);
    const [canManageOwners, setCanManageOwners] = useState(false);
    const [loading, setLoading] = useState(false);
    const [busyUserId, setBusyUserId] = useState(null);

    const [search, setSearch] = useState("");
    const [candidates, setCandidates] = useState([]);
    const [candidatesLoading, setCandidatesLoading] = useState(false);
    const [candidate, setCandidate] = useState(null);
    const [newRole, setNewRole] = useState("viewer");
    const [adding, setAdding] = useState(false);

    const roleLabel = useCallback((role) => intl.formatMessage({
        id: `projects.role_${role}`,
        defaultMessage: role.charAt(0).toUpperCase() + role.slice(1),
    }), [intl]);

    const showError = useCallback((error, fallbackId, fallback) => {
        enqueueSnackbar(
            error.response?.data?.error || intl.formatMessage({ id: fallbackId, defaultMessage: fallback }),
            { variant: "error" }
        );
    }, [enqueueSnackbar, intl]);

    const fetchMembers = useCallback(async () => {
        if (!project) return;
        try {
            setLoading(true);
            const data = await insightsApi.getProjectMembers(project.id);
            setMembers(data.members || []);
            setCanManage(!!data.can_manage_members);
            setCanManageOwners(!!data.can_manage_owners);
        } catch (error) {
            showError(error, "projects.failed_to_load_members", "Failed to load members");
        } finally {
            setLoading(false);
        }
    }, [project, showError]);

    // Reset and load whenever a different project is opened — keyed on the id
    // alone, so a re-render (a new snackbar callback, a refreshed card) does
    // not reload the list under the user.
    useEffect(() => {
        if (!project) return;
        setMembers([]);
        setCanManage(!!project.can_manage_members);
        setCanManageOwners(false);
        setCandidate(null);
        setSearch("");
        setNewRole("viewer");
        fetchMembers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [project?.id]);

    // Type-ahead over the organization's users who are not members yet.
    useEffect(() => {
        if (!project || !canManage) return undefined;
        let cancelled = false;
        const timer = setTimeout(async () => {
            try {
                setCandidatesLoading(true);
                const data = await insightsApi.getProjectMemberCandidates(project.id, search.trim());
                if (!cancelled) setCandidates(data || []);
            } catch (error) {
                if (!cancelled) setCandidates([]);
            } finally {
                if (!cancelled) setCandidatesLoading(false);
            }
        }, SEARCH_DEBOUNCE_MS);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [project?.id, canManage, search, members]);

    const handleAdd = async () => {
        if (!candidate) return;
        try {
            setAdding(true);
            await insightsApi.addProjectMember(project.id, candidate.user_id, newRole);
            enqueueSnackbar(
                intl.formatMessage(
                    { id: "projects.member_added", defaultMessage: "{name} can now see this project." },
                    { name: displayName(candidate) }
                ),
                { variant: "success" }
            );
            setCandidate(null);
            setSearch("");
            fetchMembers();
        } catch (error) {
            showError(error, "projects.failed_to_add_member", "Failed to add the member");
        } finally {
            setAdding(false);
        }
    };

    const handleRoleChange = async (member, role) => {
        if (role === member.role) return;
        try {
            setBusyUserId(member.user_id);
            const updated = await insightsApi.updateProjectMemberRole(project.id, member.user_id, role);
            setMembers((prev) => prev.map((m) => (m.user_id === member.user_id ? { ...m, ...updated } : m)));
            if (member.email === currentUserEmail) fetchMembers();
        } catch (error) {
            showError(error, "projects.failed_to_change_role", "Failed to change the role");
        } finally {
            setBusyUserId(null);
        }
    };

    const handleRemove = async (member) => {
        const isSelf = member.email === currentUserEmail;
        try {
            setBusyUserId(member.user_id);
            await insightsApi.removeProjectMember(project.id, member.user_id);
            if (isSelf) {
                enqueueSnackbar(
                    intl.formatMessage({ id: "projects.left_project", defaultMessage: "You left the project." }),
                    { variant: "success" }
                );
                onLeft?.(project);
                return;
            }
            setMembers((prev) => prev.filter((m) => m.user_id !== member.user_id));
        } catch (error) {
            showError(error, "projects.failed_to_remove_member", "Failed to remove the member");
        } finally {
            setBusyUserId(null);
        }
    };

    return (
        <Dialog open={!!project} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <FormattedMessage
                    id="projects.members_dialog_title"
                    defaultMessage="Members of {name}"
                    values={{ name: project?.name || "" }}
                />
            </DialogTitle>
            <DialogContent dividers>
                <Alert severity="info" sx={{ mb: 2 }}>
                    <FormattedMessage
                        id="projects.members_share_everything"
                        defaultMessage="Members can see every report and document in this project."
                    />
                </Alert>

                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : (
                    <List dense sx={{ border: "1px solid #e0e0e0", borderRadius: 1 }}>
                        {members.map((member) => {
                            const isSelf = member.email === currentUserEmail;
                            const busy = busyUserId === member.user_id;
                            // An editor leaves owners alone (the BE enforces it).
                            const editable = canManage && (canManageOwners || member.role !== "owner");
                            return (
                                <ListItem key={member.user_id} sx={{ pr: 1, gap: 1 }}>
                                    <ListItemText
                                        primary={
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {displayName(member)}
                                                {isSelf && (
                                                    <Typography component="span" variant="caption" color="text.secondary">
                                                        {" "}
                                                        <FormattedMessage id="projects.member_you" defaultMessage="(you)" />
                                                    </Typography>
                                                )}
                                            </Typography>
                                        }
                                        secondary={member.email}
                                        secondaryTypographyProps={{ variant: "caption" }}
                                    />
                                    {editable ? (
                                        <Select
                                            size="small"
                                            value={member.role}
                                            onChange={(e) => handleRoleChange(member, e.target.value)}
                                            disabled={busy}
                                            inputProps={{
                                                "aria-label": intl.formatMessage({ id: "projects.member_role", defaultMessage: "Role" }),
                                            }}
                                            sx={{ minWidth: 110, fontSize: "0.85rem" }}
                                        >
                                            {ROLES.map((role) => (
                                                <MenuItem key={role} value={role} disabled={role === "owner" && !canManageOwners}>
                                                    {roleLabel(role)}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    ) : (
                                        <Chip size="small" variant="outlined" label={roleLabel(member.role)} />
                                    )}
                                    {(editable || isSelf) && (
                                        <Tooltip
                                            title={isSelf
                                                ? intl.formatMessage({ id: "projects.leave_project", defaultMessage: "Leave project" })
                                                : intl.formatMessage({ id: "projects.remove_member", defaultMessage: "Remove from project" })}
                                        >
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleRemove(member)}
                                                    disabled={busy}
                                                    aria-label={isSelf
                                                        ? intl.formatMessage({ id: "projects.leave_project", defaultMessage: "Leave project" })
                                                        : intl.formatMessage({ id: "projects.remove_member", defaultMessage: "Remove from project" })}
                                                >
                                                    {busy
                                                        ? <CircularProgress size={16} />
                                                        : isSelf ? <LeaveIcon fontSize="small" /> : <PersonRemoveIcon fontSize="small" />}
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    )}
                                </ListItem>
                            );
                        })}
                        {members.length === 0 && (
                            <ListItem>
                                <ListItemText
                                    primary={
                                        <Typography variant="body2" color="text.secondary">
                                            <FormattedMessage id="projects.no_members" defaultMessage="No members yet." />
                                        </Typography>
                                    }
                                />
                            </ListItem>
                        )}
                    </List>
                )}

                {canManage && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                            <FormattedMessage id="projects.add_member" defaultMessage="Add a member" />
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", flexWrap: "wrap" }}>
                            <Autocomplete
                                sx={{ flex: "1 1 240px" }}
                                size="small"
                                options={candidates}
                                value={candidate}
                                onChange={(_, value) => setCandidate(value)}
                                inputValue={search}
                                onInputChange={(_, value, reason) => {
                                    if (reason !== "reset") setSearch(value);
                                }}
                                // The BE already filters by `search`.
                                filterOptions={(options) => options}
                                getOptionLabel={(option) => option.email}
                                isOptionEqualToValue={(option, value) => option.user_id === value.user_id}
                                loading={candidatesLoading}
                                noOptionsText={intl.formatMessage({
                                    id: "projects.no_member_candidates",
                                    defaultMessage: "No one else in your organization matches.",
                                })}
                                renderOption={(props, option) => (
                                    <li {...props} key={option.user_id}>
                                        <ListItemText
                                            primary={displayName(option)}
                                            secondary={option.email}
                                            primaryTypographyProps={{ variant: "body2" }}
                                            secondaryTypographyProps={{ variant: "caption" }}
                                        />
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder={intl.formatMessage({
                                            id: "projects.search_people",
                                            defaultMessage: "Search by name or email",
                                        })}
                                    />
                                )}
                            />
                            <Select
                                size="small"
                                value={newRole}
                                onChange={(e) => setNewRole(e.target.value)}
                                inputProps={{
                                    "aria-label": intl.formatMessage({ id: "projects.member_role", defaultMessage: "Role" }),
                                }}
                                sx={{ minWidth: 110 }}
                            >
                                {ROLES.map((role) => (
                                    <MenuItem key={role} value={role} disabled={role === "owner" && !canManageOwners}>
                                        {roleLabel(role)}
                                    </MenuItem>
                                ))}
                            </Select>
                            <Button
                                variant="contained"
                                onClick={handleAdd}
                                disabled={!candidate || adding}
                                startIcon={adding ? <CircularProgress size={16} color="inherit" /> : <PersonAddIcon />}
                                sx={{ backgroundColor: "#00438E", "&:hover": { backgroundColor: "#003A75" } }}
                            >
                                <FormattedMessage id="projects.add" defaultMessage="Add" />
                            </Button>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                            <FormattedMessage
                                id="projects.roles_hint"
                                defaultMessage="Viewers read. Editors can also rename the project and add or remove reports. Owners can also manage members and delete the project."
                            />
                        </Typography>
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose}>
                    <FormattedMessage id="projects.done" defaultMessage="Done" />
                </Button>
            </DialogActions>
        </Dialog>
    );
}
